/**
 * useDocuments — shared hook for browsing the RDL document library.
 *
 * Server status
 * -------------
 * The `/v3/documents/*` Function routes are PLANNED (not yet implemented in
 * `PCBJxChangeApi/Functions/`). Wiring spec §6.12 names the eventual shape:
 *   - `v3.documents.list({ cifId, cartId?, source? })`
 *   - `v3.documents.upload(...)`
 *
 * Until those land, this hook returns a realistic demo fixture drawn from the
 * SPFx `ao_Documents` folder taxonomy
 * (`/ao_Documents/{cifId}/cart-{cartId}/cip/{filename}`) so the RDL surface is
 * demo-ready + reviewable by PCB stakeholders. Category / CIF / cart / search
 * / sort filters run fully client-side against the fixture; the same
 * filter/sort code will continue to work once the real endpoint ships — only
 * the fetch swap changes.
 *
 * The return type matches `PortalDocumentRef` (shared-types) so the FSR and
 * RCP sides converge when the server route exists. We add two fields the FSR
 * browser needs that the RCP DTO does not: `cifId`, `cartId`, `uploadedBy`,
 * and `auroraStatus`.
 */

import { useMemo } from 'react';

/** `PortalDocumentRef.source` parity + extra RAO-generated bucket. */
export type RdlDocumentCategory =
  | 'customer_upload'
  | 'fsr_upload'
  | 'signed_pdf'
  | 'rao_output'
  | 'bank_generated';

export type RdlDocumentStatus = 'pending' | 'indexed' | 'exported' | 'failed';

export interface RdlDocumentRef {
  /** SharePoint driveItem id (stable). */
  documentId: string;
  filename: string;
  /** Bytes; for UI display via formatBytes helper. */
  sizeBytes: number;
  /** MIME — drives the file-type icon + preview support. */
  contentType: string;
  category: RdlDocumentCategory;
  /** Aurora delayed-indexing status (FSR-invisible in copy, but surfaced to admin filters). */
  auroraStatus: RdlDocumentStatus;
  /** CIF owning the document (always present — RDL is CIF-scoped). */
  cifId: string;
  /** If this doc belongs to a specific account-opening cart, the cart id. */
  cartId?: string;
  /** UPN / display name of the uploader (human or system). */
  uploadedBy: string;
  uploadedByRole?: 'Officer' | 'Customer' | 'System';
  uploadedAt: string;
  /** Relative path under `ao_Documents` for copy-link UX. */
  relativePath: string;
  /** Whether the client can stream it via pdfjs-dist inline. */
  previewable: boolean;
}

export interface UseDocumentsOptions {
  cifId?: string;
  cartId?: string;
  category?: RdlDocumentCategory | 'all';
  search?: string;
  sortBy?: 'date-desc' | 'date-asc' | 'name-asc' | 'size-desc';
}

export interface UseDocumentsResult {
  documents: ReadonlyArray<RdlDocumentRef>;
  allDocuments: ReadonlyArray<RdlDocumentRef>;
  categoryCounts: Record<RdlDocumentCategory | 'all', number>;
  isFixture: boolean;
  /** Reason banner text describing fixture vs live state. */
  fixtureNotice: string | null;
}

// ---------------------------------------------------------------------------
// Fixture data — matches SPFx DocumentCaptureModule + DocumentChecklist shapes
// ---------------------------------------------------------------------------

const FIXTURE_DOCS: ReadonlyArray<RdlDocumentRef> = [
  {
    documentId: 'doc_00001',
    filename: 'driver-license-front.jpg',
    sizeBytes: 412_880,
    contentType: 'image/jpeg',
    category: 'fsr_upload',
    auroraStatus: 'indexed',
    cifId: '0001234',
    cartId: 'cart_f7a91c',
    uploadedBy: 'ssmith@mypcb.com',
    uploadedByRole: 'Officer',
    uploadedAt: '2026-04-21T14:15:10Z',
    relativePath: '0001234/cart-f7a91c/cip/driver-license-front.jpg',
    previewable: false,
  },
  {
    documentId: 'doc_00002',
    filename: 'driver-license-back.jpg',
    sizeBytes: 389_114,
    contentType: 'image/jpeg',
    category: 'fsr_upload',
    auroraStatus: 'indexed',
    cifId: '0001234',
    cartId: 'cart_f7a91c',
    uploadedBy: 'ssmith@mypcb.com',
    uploadedByRole: 'Officer',
    uploadedAt: '2026-04-21T14:15:22Z',
    relativePath: '0001234/cart-f7a91c/cip/driver-license-back.jpg',
    previewable: false,
  },
  {
    documentId: 'doc_00003',
    filename: 'disclosure-package-signed.pdf',
    sizeBytes: 2_714_336,
    contentType: 'application/pdf',
    category: 'signed_pdf',
    auroraStatus: 'exported',
    cifId: '0001234',
    cartId: 'cart_f7a91c',
    uploadedBy: 'signnow-webhook',
    uploadedByRole: 'System',
    uploadedAt: '2026-04-21T17:42:05Z',
    relativePath: '0001234/cart-f7a91c/signed/disclosure-package-signed.pdf',
    previewable: true,
  },
  {
    documentId: 'doc_00004',
    filename: 'deposit-agreement-v14.pdf',
    sizeBytes: 187_204,
    contentType: 'application/pdf',
    category: 'rao_output',
    auroraStatus: 'indexed',
    cifId: '0001234',
    cartId: 'cart_f7a91c',
    uploadedBy: 'rao-pipeline',
    uploadedByRole: 'System',
    uploadedAt: '2026-04-21T15:03:44Z',
    relativePath: '0001234/cart-f7a91c/rendered/deposit-agreement-v14.pdf',
    previewable: true,
  },
  {
    documentId: 'doc_00005',
    filename: 'signature-card.pdf',
    sizeBytes: 95_442,
    contentType: 'application/pdf',
    category: 'rao_output',
    auroraStatus: 'indexed',
    cifId: '0001234',
    cartId: 'cart_f7a91c',
    uploadedBy: 'rao-pipeline',
    uploadedByRole: 'System',
    uploadedAt: '2026-04-21T15:03:46Z',
    relativePath: '0001234/cart-f7a91c/rendered/signature-card.pdf',
    previewable: true,
  },
  {
    documentId: 'doc_00006',
    filename: 'utility-bill-proof-of-address.pdf',
    sizeBytes: 512_910,
    contentType: 'application/pdf',
    category: 'customer_upload',
    auroraStatus: 'indexed',
    cifId: '0001234',
    uploadedBy: 'customer-portal',
    uploadedByRole: 'Customer',
    uploadedAt: '2026-04-20T22:11:02Z',
    relativePath: '0001234/utility-bill-proof-of-address.pdf',
    previewable: true,
  },
  {
    documentId: 'doc_00007',
    filename: 'trust-certification.pdf',
    sizeBytes: 1_108_774,
    contentType: 'application/pdf',
    category: 'customer_upload',
    auroraStatus: 'indexed',
    cifId: '0002997',
    cartId: 'cart_21bc4d',
    uploadedBy: 'customer-portal',
    uploadedByRole: 'Customer',
    uploadedAt: '2026-04-19T19:00:55Z',
    relativePath: '0002997/cart-21bc4d/trust-certification.pdf',
    previewable: true,
  },
  {
    documentId: 'doc_00008',
    filename: 'business-resolution.pdf',
    sizeBytes: 624_580,
    contentType: 'application/pdf',
    category: 'fsr_upload',
    auroraStatus: 'indexed',
    cifId: '0002997',
    cartId: 'cart_21bc4d',
    uploadedBy: 'bwiggins@mypcb.com',
    uploadedByRole: 'Officer',
    uploadedAt: '2026-04-19T19:14:11Z',
    relativePath: '0002997/cart-21bc4d/cip/business-resolution.pdf',
    previewable: true,
  },
  {
    documentId: 'doc_00009',
    filename: 'w9-signed.pdf',
    sizeBytes: 220_002,
    contentType: 'application/pdf',
    category: 'signed_pdf',
    auroraStatus: 'pending',
    cifId: '0002997',
    cartId: 'cart_21bc4d',
    uploadedBy: 'signnow-webhook',
    uploadedByRole: 'System',
    uploadedAt: '2026-04-19T21:42:33Z',
    relativePath: '0002997/cart-21bc4d/signed/w9-signed.pdf',
    previewable: true,
  },
  {
    documentId: 'doc_00010',
    filename: 'adverse-action-notice.pdf',
    sizeBytes: 98_112,
    contentType: 'application/pdf',
    category: 'bank_generated',
    auroraStatus: 'exported',
    cifId: '0004512',
    uploadedBy: 'rcc-adverse-action',
    uploadedByRole: 'System',
    uploadedAt: '2026-04-18T13:22:00Z',
    relativePath: '0004512/compliance/adverse-action-notice.pdf',
    previewable: true,
  },
  {
    documentId: 'doc_00011',
    filename: 'passport-scan.jpg',
    sizeBytes: 718_990,
    contentType: 'image/jpeg',
    category: 'customer_upload',
    auroraStatus: 'failed',
    cifId: '0004512',
    uploadedBy: 'customer-portal',
    uploadedByRole: 'Customer',
    uploadedAt: '2026-04-18T12:00:14Z',
    relativePath: '0004512/passport-scan.jpg',
    previewable: false,
  },
  {
    documentId: 'doc_00012',
    filename: 'beneficiary-designation-ira.pdf',
    sizeBytes: 155_408,
    contentType: 'application/pdf',
    category: 'signed_pdf',
    auroraStatus: 'indexed',
    cifId: '0004512',
    cartId: 'cart_9c31ef',
    uploadedBy: 'signnow-webhook',
    uploadedByRole: 'System',
    uploadedAt: '2026-04-18T14:55:09Z',
    relativePath: '0004512/cart-9c31ef/signed/beneficiary-designation-ira.pdf',
    previewable: true,
  },
];

const FIXTURE_NOTICE =
  'Demo fixture — /v3/documents/* routes are PLANNED. Listing, filtering, sorting, and previews run against an in-memory seed modeled on the legacy ao_Documents folder taxonomy.';

// ---------------------------------------------------------------------------
// Public hook
// ---------------------------------------------------------------------------

/**
 * Browse the document library with client-side filter/sort. Returns the
 * always-complete fixture set plus a filtered view. When the real route
 * exists this is the single seam to swap for `apiClient.retail.documents.list`.
 */
export function useDocuments(opts: UseDocumentsOptions = {}): UseDocumentsResult {
  const { cifId, cartId, category = 'all', search = '', sortBy = 'date-desc' } = opts;

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = FIXTURE_DOCS.filter((d) => {
      if (cifId && d.cifId !== cifId) return false;
      if (cartId && d.cartId !== cartId) return false;
      if (category !== 'all' && d.category !== category) return false;
      if (needle.length > 0) {
        const hay = `${d.filename} ${d.cifId} ${d.cartId ?? ''} ${d.uploadedBy}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });

    rows.sort((a, b) => {
      switch (sortBy) {
        case 'date-asc':
          return a.uploadedAt.localeCompare(b.uploadedAt);
        case 'name-asc':
          return a.filename.localeCompare(b.filename);
        case 'size-desc':
          return b.sizeBytes - a.sizeBytes;
        case 'date-desc':
        default:
          return b.uploadedAt.localeCompare(a.uploadedAt);
      }
    });

    return rows;
  }, [cifId, cartId, category, search, sortBy]);

  const categoryCounts = useMemo(() => {
    const base: Record<RdlDocumentCategory | 'all', number> = {
      all: 0,
      customer_upload: 0,
      fsr_upload: 0,
      signed_pdf: 0,
      rao_output: 0,
      bank_generated: 0,
    };
    for (const d of FIXTURE_DOCS) {
      // Scope the count bucket by cif/cart so the chip numbers match the drill-in.
      if (cifId && d.cifId !== cifId) continue;
      if (cartId && d.cartId !== cartId) continue;
      base.all += 1;
      base[d.category] += 1;
    }
    return base;
  }, [cifId, cartId]);

  return {
    documents: filtered,
    allDocuments: FIXTURE_DOCS,
    categoryCounts,
    isFixture: true,
    fixtureNotice: FIXTURE_NOTICE,
  };
}

// ---------------------------------------------------------------------------
// Helpers (exported for row components)
// ---------------------------------------------------------------------------

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export const CATEGORY_LABEL: Record<RdlDocumentCategory | 'all', string> = {
  all: 'All',
  customer_upload: 'Customer upload',
  fsr_upload: 'FSR upload',
  signed_pdf: 'Signed PDF',
  rao_output: 'RAO output',
  bank_generated: 'Bank generated',
};
